import { isPropertyWithLinkValueTypeButStringValue, } from "@buildwithflux/models";
import { lzCompressField, lzDecompressField } from "@buildwithflux/shared";
import { deepCopy, flatObjectToInterface, interfaceDataToFlatObject } from "../common";
export var ElementSchemaVersion;
(function (ElementSchemaVersion) {
    /**
     * Version 0 corresponds to the legacy element schema
     */
    ElementSchemaVersion[ElementSchemaVersion["uncompressed"] = 0] = "uncompressed";
    /**
     * Version 1 corresponds to compressing the part version data cache to achieve a reduction in the size of the IElementData at rest.
     */
    ElementSchemaVersion[ElementSchemaVersion["compressedPartVersionDataCache"] = 1] = "compressedPartVersionDataCache";
})(ElementSchemaVersion || (ElementSchemaVersion = {}));
/**
 * The schema version to write all new data with
 *
 * This is currently uncompressed so that we can upgrade all readers to support reading compressed data before writing
 * any.
 */
export const currentElementSchemaVersionToWrite = ElementSchemaVersion.compressedPartVersionDataCache;
class ElementSerializationValidationError extends Error {
    requiredField;
    elementData;
    constructor(requiredField, elementData) {
        super(`Attempted to serialize element without required field: ${requiredField} (${elementData?.uid})`);
        this.requiredField = requiredField;
        this.elementData = elementData;
    }
}
class BaseElementConverter {
    metadataGenerator;
    constructor(metadataGenerator) {
        this.metadataGenerator = metadataGenerator;
    }
    /**
     * Gets the schema version from teh stored representation (via its metadata)
     */
    static getElementSchemaVersion(stored) {
        return stored.metadata?.elementSchemaVersion || ElementSchemaVersion.uncompressed;
    }
    /**
     * Load element data from an object by delegating to functions that can read data with a given schema.
     *
     * @param rawStoredData Data of unknown type or schema version.
     * @returns An IElementData object
     * @throws Error if the schema version is not recognized.
     */
    deserialize(rawStoredData) {
        const data = deepCopy(flatObjectToInterface(rawStoredData).data);
        if (BaseElementConverter.getElementSchemaVersion(rawStoredData) >=
            ElementSchemaVersion.compressedPartVersionDataCache) {
            lzDecompressField(data, "part_version_data_cache");
        }
        const elementProperties = data?.properties;
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.keys(elementProperties).forEach((key) => {
            const elementProperty = elementProperties[key];
            if (isPropertyWithLinkValueTypeButStringValue(elementProperty)) {
                elementProperties[key].value = {
                    type: "link",
                    text: elementProperty.value,
                    url: elementProperty.value,
                };
            }
        });
        return data;
    }
    /**
     * Convert an IElementData object into an object with a schema version attached.
     */
    serialize(data, elementSchemaVersionToWrite = currentElementSchemaVersionToWrite) {
        if (!data.diagram_position) {
            throw new ElementSerializationValidationError("diagram_position", data);
        }
        // QUESTION: is this deepCopy really necessary? it is slow!
        const result = deepCopy(data);
        if (elementSchemaVersionToWrite >= ElementSchemaVersion.compressedPartVersionDataCache) {
            lzCompressField(result, "part_version_data_cache", true, true);
        }
        return interfaceDataToFlatObject({
            data: result,
            metadata: { ...this.metadataGenerator.generate(), elementSchemaVersion: elementSchemaVersionToWrite },
        });
    }
}
export class ElementAdminConverter extends BaseElementConverter {
    toFirestore(modelObject) {
        return this.serialize(modelObject);
    }
    fromFirestore(snapshot) {
        return this.deserialize(snapshot.data());
    }
}
export class ElementClientConverter extends BaseElementConverter {
    toFirestore(modelObject) {
        return this.serialize(modelObject);
    }
    fromFirestore(snapshot) {
        return this.deserialize(snapshot.data());
    }
}
//# sourceMappingURL=ElementConverter.js.map